/**
 * Кастомный диалог подтверждения вместо нативного `window.confirm()`.
 *
 * Использование:
 *
 *     import { confirmDialog } from "../components/ui";
 *
 *     const ok = await confirmDialog({
 *       title: "Удалить аккаунт?",
 *       description: "Это действие нельзя отменить.",
 *       confirmText: "Удалить",
 *       danger: true,
 *     });
 *     if (ok) { ... }
 *
 * В корне приложения должен быть смонтирован `<ConfirmDialogHost />`.
 *
 * Если действие обязано стартовать СИНХРОННО внутри обработчика клика —
 * например, оплата, которая первой же строкой делает window.open() и потеряет
 * «кредит доверия» жеста, стоит ему подождать хотя бы микротаску, — вместо
 * `await confirmDialog(...)` берите `<ConfirmDialogView>` напрямую: его
 * `onConfirm` вызывается прямо из onClick кнопки, без промежуточного промиса.
 * Разметка и тон у обоих вариантов общие — ниже она описана один раз.
 *
 * Визуально — тот же язык, что у экрана поддержки (.rla-panel/.rla-mark/
 * .rla-btn): белая карточка на --rla-r-card, знак-плашка, пара «заголовок +
 * подпись», пилюли действий. `danger` переключает палитру: деструктивное
 * действие получает плотную заливку --rla-alarm, обычное подтверждение —
 * бренд-градиент (бренд в системе закреплён за положительным действием).
 * `danger` — единственный переключатель тона:
 *   true  → знак .rla-mark-alarm (красный) + кнопка .rla-btn-danger (плотная
 *           деструктивная заливка). Удалить аккаунт, удалить переписку,
 *           заблокировать — то, что не вернуть.
 *   false → знак .rla-mark-warn (песочный) + кнопка .rla-btn-cta с бренд-
 *           градиентом. Выход из аккаунта, отправка жалобы — обратимо,
 *           поэтому бренд здесь на своём месте: это основное действие окна.
 */
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { AlertTriangle, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Portal } from "./Portal";

export interface ConfirmDialogOptions {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  /** Красная подсветка кнопки подтверждения для деструктивных действий. */
  danger?: boolean;
}

interface PendingConfirm extends ConfirmDialogOptions {
  resolve: (value: boolean) => void;
}

let openConfirmRef: ((opts: PendingConfirm) => void) | null = null;

export function confirmDialog(opts: ConfirmDialogOptions): Promise<boolean> {
  return new Promise((resolve) => {
    if (!openConfirmRef) {
      // Хост ещё не смонтирован (или fallback) — деградируем до системного confirm.
      const ok =
        typeof window !== "undefined"
          ? window.confirm(`${opts.title}${opts.description ? "\n\n" + opts.description : ""}`)
          : false;
      resolve(!!ok);
      return;
    }
    openConfirmRef({ ...opts, resolve });
  });
}

export interface ConfirmDialogViewProps extends ConfirmDialogOptions {
  open: boolean;
  /**
   * Вызывается СИНХРОННО из onClick кнопки подтверждения — без промиса между
   * жестом и действием. Это и есть причина, по которой представление вынесено
   * из хоста: оплата должна успеть открыть вкладку, пока жив жест.
   */
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Представление окна подтверждения. Единственная копия разметки: и
 * `ConfirmDialogHost` (промис-API), и прямые потребители рисуют это.
 */
export function ConfirmDialogView({
  open,
  title,
  description,
  confirmText,
  cancelText,
  danger,
  onConfirm,
  onCancel,
}: ConfirmDialogViewProps) {
  const reduceMotion = useReducedMotion();

  // Esc → Отмена.
  //
  // Слушаем в фазе ПЕРЕХВАТА и глушим всплытие: ModalShell вешает свой
  // window-обработчик Escape в фазе всплытия, и без этого один Escape закрывал
  // бы разом и подтверждение, и модалку под ним (магазин пакетов, тарифы).
  // Отмена подтверждения не должна уносить с собой экран, с которого её
  // вызвали.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onCancel();
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [open, onCancel]);

  return (
    /*
     * Портал в body: #root стоит в position:fixed и образует stacking context,
     * поэтому z-[200] изнутри него проигрывал модалкам, порталированным в body
     * (их z-80 сравнивается с z-0 самого #root, а не с этим оверлеем).
     */
    <Portal>
    <AnimatePresence>
      {open && (
        <motion.div
          key="confirm-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          /* z-[400]: выше воронки анализа (ModalShell zLayer 300–304). На z-[200]
             подтверждение «Обновить разбор» открывалось ПОД окном разбора —
             его не было видно, а кнопка навсегда оставалась «Обновляем…» (#1165). */
          className="rla-dialog-scrim z-[400]"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onClick={(e) => {
            // Portal уносит узел в body, но события React всплывают по дереву
            // КОМПОНЕНТОВ: без этого клик по затемнению долетал бы до модалки,
            // внутри которой отрисовано подтверждение.
            e.stopPropagation();
            onCancel();
          }}
        >
          {/* Материализация: масштаб и прозрачность вместе, пружина без
              отскока (bounce 0) — окно подтверждения не место для игривости. */}
          <motion.div
            initial={{ scale: reduceMotion ? 1 : 0.94, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: reduceMotion ? 1 : 0.94, opacity: 0 }}
            transition={reduceMotion ? { duration: 0.15 } : { type: "spring", bounce: 0, duration: 0.32 }}
            className="rla-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Закрыть"
              className="rla-iconbtn rla-dialog-close"
              onClick={onCancel}
            >
              <X className="size-[18px]" />
            </button>

            {/* Знак один и тот же, тон — разный: опасное действие красное,
                обратимое («Выйти?», «Отправить жалобу?») — тёплое песочное.
                Красный на выходе из аккаунта обесценил бы красный на
                удалении, а именно он там должен останавливать. */}
            <span className={`rla-mark ${danger ? "rla-mark-alarm" : "rla-mark-warn"}`} aria-hidden="true">
              <AlertTriangle className="size-[26px]" strokeWidth={2.1} />
            </span>
            <h3 className="rla-dialog-title">{title}</h3>
            {description && <p className="rla-dialog-text">{description}</p>}

            <div className="rla-dialog-actions">
              <button
                type="button"
                autoFocus
                className={`rla-btn rla-btn-full ${danger ? "rla-btn-danger" : "rla-btn-cta"}`}
                onClick={onConfirm}
              >
                {confirmText || "Подтвердить"}
              </button>
              <button
                type="button"
                className="rla-btn rla-btn-line rla-btn-full"
                onClick={onCancel}
              >
                {cancelText || "Отмена"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </Portal>
  );
}

/**
 * Хост промис-API: держит текущий запрос `confirmDialog()` и отдаёт ответ.
 * Монтируется один раз в корне приложения.
 */
export function ConfirmDialogHost() {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  // Ответ отдаём через ref, а не из апдейтера setState: апдейтер React может
  // вызвать дважды (StrictMode), а побочный эффект внутри него — это как раз
  // тот случай, когда «дважды» становится заметно.
  const pendingRef = useRef<PendingConfirm | null>(null);

  useEffect(() => {
    openConfirmRef = (opts) => {
      pendingRef.current = opts;
      setPending(opts);
    };
    return () => {
      openConfirmRef = null;
    };
  }, []);

  const settle = useCallback((value: boolean) => {
    pendingRef.current?.resolve(value);
    pendingRef.current = null;
    setPending(null);
  }, []);

  const confirm = useCallback(() => settle(true), [settle]);
  const cancel = useCallback(() => settle(false), [settle]);

  return (
    <ConfirmDialogView
      open={!!pending}
      title={pending?.title ?? ""}
      description={pending?.description}
      confirmText={pending?.confirmText}
      cancelText={pending?.cancelText}
      danger={pending?.danger}
      onConfirm={confirm}
      onCancel={cancel}
    />
  );
}
